import { Location } from '@angular/common';
import { DOCUMENT, Inject, Injectable, inject } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { IModelDataV3, IModelDataV3WithUrl, Model } from '@df/catalog/model';
import { Route } from '@df/catalog/route/route';
import { RouteResolver } from '@df/catalog/route/route.resolver';
import { App } from '@df/core/app';
import { HeadService, NHead } from '@df/core/meta/head/head.service';
import { HTTPStatus, MetaState } from '@df/core/meta/meta-state';
import { MetaStateFactory } from '@df/core/meta/meta-state.factory';
import { StructuralData } from '@df/core/meta/structural-data/structural-data.namespace';
import { Client } from '@df/core/service/client';
import { DfEventService } from '@df/core/service/df-event';
import { Logger } from '@df/core/service/logger';
import { TimeoutService } from '@df/ng/timeout.service';
import { captureException } from '@sentry/angular';
import difference from 'lodash-es/difference';
import { Subject, Subscription } from 'rxjs';
import { NCore } from '../df-core.interfaces';

export * from '@df/core/meta/meta-state';

declare module '@df/core/service/df-event' {
    interface IEventType {
        /**
         * Any GTM event pushed to data layer
         */
        dfViewModelChange: DfEventConfig<'dfViewModelChange', IDfViewModelChangeEvent, void>;

        /**
         * Meta state was updated
         */
        dfMetaStateChange: DfEventConfig<
            'dfMetaStateChange',
            {
                state: MetaState | undefined;
            },
            void
        >;
    }

    interface IDfViewModelChangeEvent {
        model: Model | undefined;
    }
}

@Injectable({
    providedIn: 'root'
})
export class Meta {
    /**
     * Changes notification subject
     */
    readonly subject = new Subject<MetaState | undefined>();

    /**
     * Previous meta state
     */
    previousState?: MetaState;

    /**
     * Current state
     */
    state?: MetaState;

    busy = true;

    /**
     * Metas existing now managed by this script
     */
    private existingTags: HTMLMetaElement[] = [];

    /**
     * Existing title element
     */
    private existingTitle?: HTMLTitleElement;

    /**
     * Existing canonical element
     */
    private existingCanonical?: HTMLLinkElement;

    /**
     * If refferred has been patched alread
     * we need to patch it to provide last page as referrer
     * and not what lead to the website
     * @type {boolean}
     */
    private referrerPatched = false;
    /**
     * Previous page we visited
     * We will pass it as referrer
     *
     * @type {boolean}
     */
    private referrer?: string;

    /**
     * Filtered router events to ignore hash
     */
    readonly routerEvents = new Subject<NavigationStart | NavigationEnd | NavigationError | NavigationCancel>();
    readonly routerEventsSync = new Subject<NavigationStart | NavigationEnd | NavigationError | NavigationCancel>();

    currentRoute: Route | undefined;

    // this cannot be async @see https://tomandco.atlassian.net/browse/OBR-2189
    private readonly onNextCallback = () => {
        if (this.app.data) {
            this._onNext();
        } else {
            this.dfApiConfigService
                .loadApiConfig()
                .then(() => this.onNext())
                .catch(e => this.logger.error(e));
        }
    };

    // debounce only in the browser; on the server run synchronously
    onNext = this.app.isPlatformBrowser ? this.timeoutService.debounce(this.onNextCallback, 100) : this.onNextCallback;

    /**
     * Do not move to constructor
     * we want it only on demand
     */
    private readonly routeResolver = inject(RouteResolver);
    private readonly location = inject(Location);
    private readonly logger = inject(Logger);

    private langSet = false;

    private lastModel?: Model;

    protected dfEventService = inject(DfEventService);
    protected dfCoreOptions = inject(NCore.OPTIONS);
    protected headService = inject(HeadService);

    private stateSubscription?: Subscription;

    /**
     * Service initiation
     */
    constructor(
        private router: Router,
        private client: Client,
        private app: App,
        private dfApiConfigService: DfApiConfigService,
        protected metaStateFactory: MetaStateFactory,
        private timeoutService: TimeoutService,
        @Inject(DOCUMENT) readonly document: Document
    ) {
        app.meta = this;

        this.router.events.subscribe(e => this.onRouterChange(e));
        this.subject.subscribe(() => {
            const model = this.model;
            if (this.lastModel !== model) {
                this.lastModel = model;
                this.dfEventService.dispatch({ id: 'dfViewModelChange', data: { model } });
            }
            this.onNext();
        });

        this.existingTags = this.headService.findElements<HTMLMetaElement>('meta[id^="df-meta-"]');
    }

    private setLang() {
        if (!this.langSet) {
            this.langSet = true;
            this.dfApiConfigService
                .loadApiConfig()
                .then(() => {
                    const lang = this.app.multisite.current.lang?.find(i => i !== 'x-default');
                    if (lang) {
                        this.document.documentElement?.setAttribute('lang', lang);
                    }
                })
                .catch(e => this.logger.error(e));
        }
    }

    /**
     * Subscribe sorthand
     */
    subscribe(...args) {
        return this.subject.subscribe(...args);
    }

    /**
     * Router events listener
     */
    onRouterChange(event): void {
        if (!(
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationError ||
            event instanceof NavigationCancel
        )) {
            return;
        }
        this.setLang();
        const path = event.url ? this.routeResolver.cleanUrl(event.url) : undefined;
        // no changes if path hasnt changed
        if (typeof path === 'string' && this.state?.path === path) {
            return;
        }
        if (event instanceof NavigationStart) {
            this.busy = true;
            this.previousState = this.state;
            this.setState(undefined);
            this.currentRoute = undefined;
            this.fixReferrer();
        } else if (event instanceof NavigationEnd) {
            this.busy = false;
            this.nextState(this.routeResolver.cleanUrl(event.url));
        } else if (event instanceof NavigationError) {
            console.error('Navigation error', event);
            const error = event.error instanceof Error ? event.error.message : event.error;
            captureException('Navigation error', { extra: { event, error } });
            this.applyStateOverride(event.url, 'error');
        }
        this.timeoutService.setTimeout(() => {
            this.routerEvents.next(event);
        });
        this.routerEventsSync.next(event);
    }

    private setState(state: MetaState | undefined): void {
        this.state?.destroy();
        // OBR-9232: state must be set synchronously before dispatch.
        // dfEventService.dispatch() is async — its action callback is deferred
        // to the microtask queue. nextState() calls subject.next(this.state)
        // right after setState(), so this.state must already be assigned,
        // otherwise subscribers (e.g. BreadcrumbsComponentDef) receive undefined.
        this.state = state;
        this.stateSubscription?.unsubscribe();
        this.stateSubscription = this.state?.model$.subscribe(model => {
            this.subject.next(this.state);
        });

        this.dfEventService.dispatch({
            id: 'dfMetaStateChange',
            data: { state },
            action: () => {
                if (this.currentRoute && this.state && this.state.route !== this.currentRoute) {
                    this.onUpdateRoute();
                }
            }
        });
    }

    updateRoute(route: Route) {
        this.currentRoute = route;

        if (this.state) {
            this.onUpdateRoute();
        }
    }

    private onUpdateRoute(): void {
        const route = this.currentRoute;
        if (this.state && route) {
            this.state.route = route;
            this.state.metaData = undefined;
            this.subject.next(this.state);
        }
    }

    applyStateOverride(originalUrl: string, state: string) {
        this.setState(
            this.metaStateFactory.getMetaState(this.routeResolver.cleanUrl(originalUrl), this.currentRoute, undefined, HTTPStatus.error)
        );
        this.busy = false;
        this.subject.next(this.state);
        // show 500 page
        this.router.navigate([state], { skipLocationChange: true });
        if (this.state?.url) {
            this.location.replaceState(this.state.url);
        }
    }

    nextState(url: string) {
        this.setState(this.metaStateFactory.getMetaState(url, this.currentRoute, this.previousState));
        this.busy = false;
        this.subject.next(this.state);
    }

    /**
     * Current status
     */
    get status(): HTTPStatus | undefined {
        if (this.busy) {
            return undefined;
        }
        const state = this.state;
        return state ? state.status : HTTPStatus.error;
    }

    get model(): Model<IModelDataV3WithUrl> | undefined {
        const state = this.state;
        return state ? state.model : undefined;
    }

    set model(model: Model<IModelDataV3WithUrl> | undefined) {
        const state = this.state;
        if (state) {
            if (state.model !== model) {
                state.model = model;
            }
        }
    }

    /**
     * Fixes referrer
     */
    private fixReferrer(): void {
        if (typeof document === 'undefined') {
            return;
        }
        // keep previous patch
        if (this.previousState) {
            const url = this.previousState.url.replace(/\~2F/g, '/');
            this.referrer = this.app.url.toUrl(url);
        }
        // -- patching referrer
        // https://plan.tomandco.uk/browse/CWB-384
        if (!this.referrerPatched && this.referrer !== undefined) {
            this.referrerPatched = true;
            Object.defineProperty(document, 'referrer', {
                get: () => this.referrer
            });
        }
    }

    private _onNext() {
        const tags: HTMLMetaElement[] = [];
        const state = this.state;

        const model = this.model;

        const add = (metaDefinition: { name: string; content?: string } | { property: string; content?: string }) => {
            if (!metaDefinition.content && !metaDefinition['property']) {
                return;
            }

            const attributes: NHead.IMetaConfig['attributes'] = {
                id: 'df-meta-' + ('name' in metaDefinition ? metaDefinition.name : metaDefinition.property).replace(/[^a-zA-Z0-9]/g, '-')
            };
            if ('name' in metaDefinition && metaDefinition.name) {
                attributes.name = metaDefinition.name;
            }
            if ('property' in metaDefinition && metaDefinition.property) {
                attributes.property = metaDefinition.property;
            }
            if (metaDefinition.content) {
                attributes.content = metaDefinition.content;
            }

            const tag = this.headService.add({
                element: 'meta',
                ssr: true,
                attributes
            });

            tags.push(tag);
        };

        if (this.client.mode === 'legacyPrerender') {
            add({
                name: 'prerender-status-code',
                content: this.status ? '' + this.status : ''
            });
            const route = state ? state.route : undefined;
            if (route && route.data && route.data.rewrite) {
                add({
                    name: 'prerender-header',
                    content: 'Location:' + this.app.url.toUrl(route.data.rewrite)
                });
            }
            add({ name: 'display-size', content: this.client.size });
            add({
                name: 'model',
                content: model ? model.type + ':' + model.id : ''
            });
        } else {
            add({
                name: 'status-code',
                content: this.status ? '' + this.status : ''
            });
            if (this.client.mode === 'browser') {
                add({ name: 'fragment', content: '!' });
            }
        }

        const locale = this.app.multisite.current.contentLang || this.app.data.locale || 'en_GB';
        add({ name: 'Content-Language', content: locale });
        const fullPath = this.location.path();
        const searchParams = fullPath.split('?')[1];
        let robots = state ? state.robots : '';

        if (searchParams) {
            const params = new URLSearchParams(searchParams);
            if (this.dfCoreOptions.meta.searchParamsForNoIndex.some(param => params.has(param))) {
                robots = 'NOINDEX, FOLLOW';
            }
        }

        if (this.status !== HTTPStatus.ok || (this.dfCoreOptions.meta.detectEmailForNoIndex && this.containsEmail(fullPath))) {
            robots = 'NOINDEX, FOLLOW';
        }

        add({ name: 'robots', content: robots });

        // site name
        if (this.app.multisite.current?.siteName) {
            add({ property: 'og:site_name', content: this.app.multisite.current?.siteName });
        }

        // change title
        if (!this.existingTitle) {
            this.existingTitle =
                this.headService.findElement<HTMLTitleElement>('title') ||
                this.headService.add({
                    element: 'title',
                    ssr: true
                });
        }

        const title = this.getMetaProperty('title') || this.getMetaProperty('name');
        this.headService.renderer2.setProperty(this.existingTitle, 'innerText', title);

        if (model?.data) {
            let description = this.getMetaProperty('description');
            if (description && typeof description !== 'string') {
                description = String(description);
            }
            add({ name: 'description', content: description });
            add({ name: 'title', content: title });
            add({ name: 'keywords', content: this.getMetaProperty('keywords') });
            add({ property: 'og:type', content: this.getMetaProperty('ogtype') });
            add({ property: 'og:title', content: title });
            add({
                property: 'og:description',
                content: description ? description.replace(/<[^>]+>/gm, '') : description
            });
            // add({
            //     property: 'og:image',
            //     content: this.resizeService.resize(this.app.url.toMediaUrl(this.getMetaProperty('ogimage')
            // || this.getMetaProperty('image')), <IResizeOptions>{
            //         width: 500,
            //         height: 500,
            //         fitIn: true
            //     })
            // });
            add({
                property: 'og:image',
                content: this.app.url.toMediaUrl(this.getMetaProperty('ogimage') || this.getMetaProperty('image'))
            });
            add({ property: 'og:image:type', content: this.getMetaProperty('ogimageType') });
            add({ property: 'og:image:width', content: this.getMetaProperty('ogimageWidth') });
            add({ property: 'og:image:height', content: this.getMetaProperty('ogimageHeight') });
            add({ property: 'og:url', content: this.app.url.toUrl(this.getMetaProperty('url')) });
            add({ property: 'og:locale', content: locale });
            add({ name: 'twitter:url', content: this.app.url.toUrl(this.getMetaProperty('url')) });
            add({ name: 'twitter:title', content: this.getMetaProperty('title') || this.getMetaProperty('name') });
            add({ name: 'twitter:description', content: this.getMetaProperty('description') });
            add({
                name: 'twitter:image',
                content: this.app.url.toMediaUrl(this.getMetaProperty('ogimage') || this.getMetaProperty('image'))
            });

            if (model.type === 'product') {
                add({ property: 'og:price:amount', content: this.getMetaProperty('price') });
                add({
                    property: 'og:price:standard_amount',
                    content: this.getMetaProperty('org_price')
                });
                add({
                    property: 'og:price:currency',
                    content: this.app.data.currency.base
                });
                add({
                    property: 'og:availability',
                    content: this.getProductAvailability(<number>model.id, true)
                });
            }
        }

        // remove previous and set new ones
        difference(this.existingTags, tags).forEach(tag => this.headService.remove(tag));
        this.existingTags = tags;
        // change canonical

        let canonical: string | undefined;

        /**
         * Never set canonical fo 404 pages, it always is the current page
         * NOTE: SSR side caches single 400 entry for multiple paths
         */
        if (this.state?.status !== HTTPStatus.notfound) {
            canonical =
                (state &&
                    (this.getMetaProperty('canonical', state.model || state.route?.target) ||
                        this.getMetaProperty('url', state.model || state.route?.target))) ||
                this.location.path().split('?')[0];
            const allowedCanonicalParams = state?.extras?.allowedCanonicalParams || [];
            if (allowedCanonicalParams.length > 0) {
                const params = new URLSearchParams(this.location.path().split('?')[1]);
                const canonicalParams = allowedCanonicalParams
                    .map(param => {
                        if (params.has(param)) {
                            return param + '=' + params.get(param);
                        }
                        return null;
                    })
                    .filter(Boolean)
                    .join('&');
                if (canonicalParams) {
                    canonical = canonical + '?' + canonicalParams;
                }
            }
        }
        if (canonical) {
            if (!this.existingCanonical) {
                this.existingCanonical = this.headService.add({
                    element: 'link',
                    ssr: true,
                    attributes: {
                        id: 'canonical',
                        rel: 'canonical'
                    }
                });
            }
            this.headService.renderer2.setAttribute(this.existingCanonical, 'href', this.app.url.toUrl(canonical));
        } else {
            if (this.existingCanonical) {
                this.headService.remove(this.existingCanonical);
                delete this.existingCanonical;
            }
        }
    }

    /**
     * Retrieves product availability string
     */
    getProductAvailability(productId: number, fbSyntax: boolean = false): string | StructuralData.ItemAvailability {
        const product = this.app.resources.product.getExisting(productId);
        let stockStatus: number;
        if (!product) {
            return '';
        }
        if (product.isConfigurable || product.isBundle) {
            stockStatus = product.isOutOfStock ? 0 : 1;
        } else {
            const stockModel = product.stock;
            stockStatus = stockModel ? stockModel.checkStockStatus(1) : 0;
        }

        if (stockStatus > 0) {
            return fbSyntax ? 'instock' : StructuralData.ItemAvailability.inStock;
        }
        if (stockStatus < 0) {
            return fbSyntax ? 'instock' : StructuralData.ItemAvailability.preOrder;
        }
        return fbSyntax ? 'oos' : StructuralData.ItemAvailability.outOfStock;
    }

    /**
     * Retrieves proprty from meta object or model data
     */
    getMetaProperty(attr: string, model?: Model<IModelDataV3>): any {
        const state = this.state;
        if (model && !(state?.model === model)) {
            const data = model.rawData;
            return data && ((data.meta && data.meta[attr]) || data[attr]);
        }
        if (state) {
            if (state.metaData && state.metaData[attr] !== undefined) {
                return state.metaData[attr];
            }
            const md = this.model;
            if (md && md.rawData && md.rawData[attr]) {
                return md.rawData[attr];
            }
        }
        return '';
    }

    /**
     * Checks if string contains email address
     */
    private containsEmail(text: string): boolean {
        const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;

        return emailRegex.test(text);
    }
}

declare module '@df/core/app' {
    interface App {
        meta: Meta;
    }
}
